import { useEffect, useState, useCallback } from "react";
import { CalendarDays, Scissors, Clock, Trash2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Vista pública de la agenda en tiempo real (próximas 3 semanas).
// Con ?pin= (link privado del titolare) aparece el botón de borrar citas.
export default function AgendaPage({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [pin] = useState(() => new URLSearchParams(window.location.search).get("pin") || "");

  const load = useCallback(() => {
    fetch(`${BACKEND}/api/agenda/${token}${pin ? `?pin=${pin}` : ""}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setError(true));
  }, [token, pin]);

  useEffect(() => {
    load();
    // Auto-refresh cada 60 segundos para que el socio vea cambios en tiempo real
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);

  const deleteAppt = async (id, nome) => {
    if (!window.confirm(`Eliminare l'appuntamento di ${nome}?`)) return;
    const res = await fetch(`${BACKEND}/api/agenda/${token}/booking/${id}?pin=${pin}`, {
      method: "DELETE",
    });
    if (res.ok) load();
  };

  if (error) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-zinc-950 px-8 text-center">
        <p className="text-sm text-zinc-500">Link non valido o scaduto.</p>
      </div>
    );
  }

  return (
    <div data-testid="page-agenda" className="min-h-[100dvh] bg-zinc-950 text-white">
      <div className="mx-auto flex min-h-[100dvh] w-full max-w-md flex-col gap-5 px-6 py-8">
        <header className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-400/15 text-amber-400">
            <CalendarDays size={20} />
          </span>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold">Agenda — 1 mese</h1>
            <p className="text-xs text-zinc-500">
              {data?.salone || "…"} — {data?.can_manage ? "modalità gestione" : "solo lettura"}
              {data?.aggiornata && ` · agg. ${data.aggiornata}`}
            </p>
          </div>
        </header>

        {data && (
          <div data-testid="agenda-summary" className="rounded-2xl border border-white/10 bg-zinc-900 p-4">
            <div className="mb-3 grid grid-cols-2 gap-2">
              <div className="rounded-xl bg-zinc-950 p-3 text-center">
                <p data-testid="agenda-total" className="text-2xl font-extrabold text-white">
                  {data.totale_appuntamenti}
                </p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Appuntamenti mese
                </p>
              </div>
              <div className="rounded-xl bg-zinc-950 p-3 text-center">
                <p className="text-2xl font-extrabold text-amber-400">{data.team.length}</p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Barbieri attivi
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              {Object.entries(data.per_barbiere).map(([name, count]) => (
                <div key={name} className="flex items-center gap-2 rounded-lg bg-zinc-950 px-3 py-2">
                  <Scissors size={12} className="shrink-0 text-amber-400" />
                  <span className="min-w-0 flex-1 truncate text-xs font-bold text-white">{name}</span>
                  <span className="text-[11px] text-zinc-400">
                    {count} {count === 1 ? "appuntamento" : "appuntamenti"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!data && <p className="text-sm text-zinc-500">Caricamento…</p>}

        {data?.giorni.map((g) => (
          <section
            key={g.label}
            data-testid={`agenda-day-${g.label.replace(/\s/g, "-")}`}
            className="rounded-2xl border border-white/10 bg-zinc-900 p-4"
          >
            <p className="mb-3 text-xs font-bold uppercase tracking-widest text-zinc-400">
              {g.label} {g.closed && <span className="text-red-400">· Chiuso</span>}
            </p>
            {!g.closed && (
              <>
                {g.appuntamenti.length === 0 ? (
                  <p className="text-xs text-zinc-600">Nessun appuntamento.</p>
                ) : (
                  <div className="mb-3 flex flex-col gap-2">
                    {g.appuntamenti.map((a, i) => (
                      <div
                        key={i}
                        data-testid={`agenda-appt-${g.label}-${i}`}
                        className="flex items-center gap-3 rounded-xl bg-zinc-950 px-3 py-2.5"
                      >
                        <span className="w-12 shrink-0 text-sm font-extrabold text-amber-400">
                          {a.ora}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-bold text-white">{a.cliente}</p>
                          <p className="text-[11px] text-zinc-500">{a.servizio}</p>
                        </div>
                        <span className="flex shrink-0 items-center gap-1 rounded-full bg-white/5 px-2 py-1 text-[10px] font-bold text-zinc-300">
                          <Scissors size={10} className="text-amber-400" />
                          {a.barbiere}
                        </span>
                        {data.can_manage && a.id && (
                          <button
                            data-testid={`agenda-del-${i}`}
                            onClick={() => deleteAppt(a.id, a.cliente)}
                            className="shrink-0 rounded-lg bg-red-500/10 p-2 text-red-400 transition-colors hover:bg-red-500/25"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {g.liberi.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {g.liberi.map((f) => (
                      <span
                        key={f}
                        className="flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-1 text-[10px] font-bold text-emerald-400"
                      >
                        <Clock size={9} /> {f}
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        ))}
        <p className="pb-4 text-center text-[10px] text-zinc-700">
          Vista condivisa di sola lettura — i dati dei clienti restano privati.
        </p>
      </div>
    </div>
  );
}
